import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { MsalProvider } from '@azure/msal-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { msalInstance } from '@/features/auth/authConfig'
import { queryClient } from '@/lib/queryClient'
import { MockAuthProvider } from '@/features/auth/MockAuthProvider'
import { ProtectedRoute } from '@/features/auth/ProtectedRoute'
import { useInactivityLogout } from '@/features/auth/useInactivityLogout'
import { AppLayout } from '@/app/AppLayout'
import { ErrorLayout } from '@/app/ErrorLayout'
import { ErrorBoundary } from '@/app/ErrorBoundary'

/** True when VITE_DISABLE_AUTH=true is set in .env.local (dev only). */
const disableAuth = import.meta.env.VITE_DISABLE_AUTH === 'true'

/** Swaps MsalProvider for MockAuthProvider when auth is disabled. */
function AuthWrapper({ children }: { children: ReactNode }) {
  if (disableAuth) return <MockAuthProvider>{children}</MockAuthProvider>
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>
}

/**
 * providers.tsx
 * Pattern from Mable_Scaffold: all providers + BrowserRouter + Routes live here.
 * Provider order:
 *   ErrorBoundary → MsalProvider → QueryClientProvider → BrowserRouter → Routes
 *   Toaster sits outside BrowserRouter so toasts survive navigation.
 *
 * Route structure:
 *   /access_denied   — public error page
 *   /Unauthorized    — public error page
 *   /*               — ProtectedRoute wraps AppLayout; child routes render via <Outlet />
 */

// ── Error / public pages ─────────────────────────────────────────────────────
const AccessDeniedPage   = lazy(() => import('@/features/errors/AccessDeniedPage').then(m => ({ default: m.AccessDeniedPage })))
const UnauthorizedPage   = lazy(() => import('@/features/errors/UnauthorizedPage').then(m => ({ default: m.UnauthorizedPage })))

// ── Dashboard ─────────────────────────────────────────────────────────────────
const DashboardPage      = lazy(() => import('@/features/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })))
const DashboardGraphPage = lazy(() => import('@/features/dashboard/DashboardGraphPage').then(m => ({ default: m.DashboardGraphPage })))

// ── Attendance ────────────────────────────────────────────────────────────────
const ProgrammePage              = lazy(() => import('@/features/attendance/ProgrammePage').then(m => ({ default: m.ProgrammePage })))
const SessionPage                = lazy(() => import('@/features/attendance/SessionPage').then(m => ({ default: m.SessionPage })))
const AttendanceTrackingPage     = lazy(() => import('@/features/attendance/AttendanceTrackingPage').then(m => ({ default: m.AttendanceTrackingPage })))
const AttendanceDashboardPage    = lazy(() => import('@/features/attendance/AttendanceDashboardPage').then(m => ({ default: m.AttendanceDashboardPage })))
const AttendanceSummaryPage      = lazy(() => import('@/features/attendance/AttendanceSummaryPage').then(m => ({ default: m.AttendanceSummaryPage })))
const AttendeeScanPage           = lazy(() => import('@/features/attendance/AttendeeScanPage').then(m => ({ default: m.AttendeeScanPage })))
const AttendeeSessionDetailsPage         = lazy(() => import('@/features/attendance/AttendeeSessionDetailsPage').then(m => ({ default: m.AttendeeSessionDetailsPage })))
const AttendeeSessionScanPage            = lazy(() => import('@/features/attendance/AttendeeSessionScanPage').then(m => ({ default: m.AttendeeSessionScanPage })))
const AttendeeListPage                   = lazy(() => import('@/features/attendance/AttendeeListPage').then(m => ({ default: m.AttendeeListPage })))
const AttendanceRegistrationSummaryPage  = lazy(() => import('@/features/attendance/AttendanceRegistrationSummaryPage').then(m => ({ default: m.AttendanceRegistrationSummaryPage })))

// ── Capacity management ───────────────────────────────────────────────────────
const CapacityManagementPage  = lazy(() => import('@/features/capacity-management/CapacityManagementPage').then(m => ({ default: m.CapacityManagementPage })))
const SessionCapacityLogPage  = lazy(() => import('@/features/capacity-management/SessionCapacityLogPage').then(m => ({ default: m.SessionCapacityLogPage })))
const SessionLogPage          = lazy(() => import('@/features/capacity-management/SessionLogPage').then(m => ({ default: m.SessionLogPage })))

// ── Queue ─────────────────────────────────────────────────────────────────────
const OccupancyQueueMasterPage = lazy(() => import('@/features/queue/OccupancyQueueMasterPage').then(m => ({ default: m.OccupancyQueueMasterPage })))

// ── Admin ─────────────────────────────────────────────────────────────────────
const AuditLogPage     = lazy(() => import('@/features/audit-log/AuditLogPage').then(m => ({ default: m.AuditLogPage })))
const StaffBadgingPage = lazy(() => import('@/features/staff-badging/StaffBadgingPage').then(m => ({ default: m.StaffBadgingPage })))
const UserAccessPage   = lazy(() => import('@/features/user-access/UserAccessPage').then(m => ({ default: m.UserAccessPage })))

// ── Root redirect ───────────────────────────────────────────────────────────
/**
 * Mirrors Blazor Dashboard.razor.cs GotoURL():
 *   - DUWL (or both DUWL+DUIN, default) → /dashboard
 *   - DUIN only → /attendancetracking
 * Waits until bootstrap is complete before redirecting.
 */
function RootRedirect() {
  const tenant          = useAuthStore((s) => s.tenant)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Still bootstrapping — stay put; ProtectedRoute will show spinner
  if (!isAuthenticated) return null

  const to = tenant === 'DUIN' ? 'attendancetracking' : 'dashboard'
  return <Navigate to={to} replace />
}

// ── Suspense spinner ──────────────────────────────────────────────────────────
function PageSpinner() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-6">
      <div className="du-loader" aria-hidden="true" />
      <span className="text-sm text-[#53565a]">Loading...</span>
    </div>
  )
}

function InactivityLogoutController() {
  useInactivityLogout()
  return null
}

// ── Root provider tree ────────────────────────────────────────────────────────
export function AppProviders() {
  return (
    <ErrorBoundary>
      <AuthWrapper>
        <InactivityLogoutController />
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            <Suspense fallback={<PageSpinner />}>
              <Routes>
                {/* ── Public error pages ─────────────────────────────────── */}
                <Route element={<ErrorLayout />}>
                  <Route path="/access_denied" element={<AccessDeniedPage />} />
                  <Route path="/Unauthorized"  element={<UnauthorizedPage />} />
                </Route>

                {/* ── Authenticated routes ────────────────────────────────── */}
                <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                  <Route index                                          element={<RootRedirect />} />
                  <Route path="dashboard"                               element={<DashboardPage />} />
                  <Route path="dashboardgraph"                          element={<DashboardGraphPage />} />
                  <Route path="programme"                               element={<ProgrammePage />} />
                  <Route path="session"                                 element={<SessionPage />} />
                  <Route path="attendancetracking"                      element={<AttendanceTrackingPage />} />
                  <Route path="attendancedashboard"                     element={<AttendanceDashboardPage />} />
                  <Route path="Attendancesummary"                       element={<AttendanceSummaryPage />} />
                  <Route path="attendee-scan-details"                   element={<AttendeeScanPage />} />
                  <Route path="attendee-session-details"                element={<AttendeeSessionDetailsPage />} />
                  <Route path="attendee-session-scan-details"           element={<AttendeeSessionScanPage />} />
                  <Route path="attendeelist"                             element={<AttendeeListPage />} />
                  <Route path="attendanceregistrationsummary"            element={<AttendanceRegistrationSummaryPage />} />
                  <Route path="capacity-management"                     element={<CapacityManagementPage />} />
                  <Route path="sessioncapacitylog"                      element={<SessionCapacityLogPage />} />
                  <Route path="sessionlog"                              element={<SessionLogPage />} />
                  <Route path="occupancy-queue-master"                  element={<OccupancyQueueMasterPage />} />
                  <Route path="auditlog"                                element={<AuditLogPage />} />
                  <Route path="staff-badging"                           element={<StaffBadgingPage />} />
                  <Route path="user-access-management"                  element={<UserAccessPage />} />
                </Route>

                {/* ── Catch-all ───────────────────────────────────────────── */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>

          {/* Toaster outside BrowserRouter — survives navigation */}
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 5000,
              style: { borderRadius: '4px', background: '#53565a', color: '#fff' },
              success: { style: { background: '#43B02A' } },
              error:   { style: { background: '#DA291C' } },
            }}
          containerStyle={{ top: '62px' }}
          containerClassName="toast-center-screen"
          />
        </QueryClientProvider>
      </AuthWrapper>
    </ErrorBoundary>
  )
}
